import { useState, useEffect, useCallback, useRef } from 'react';
import { AuthProvider, useAuth } from './lib/auth';
import { supabase } from './lib/constants';
import AuthPage from './components/AuthPage';
import Sidebar from './components/Sidebar';
import MobileNav from './components/MobileNav';
import HomePage from './components/HomePage';
import HomeEditor from './components/HomeEditor';
import Dashboard from './components/Dashboard';
import EntryForm from './components/EntryForm';
import CaseLog from './components/CaseLog';
import Progress from './components/Progress';
import DirectorDashboard from './components/DirectorDashboard';
import FacultyDashboard from './components/FacultyDashboard';
import ActivityForm from './components/ActivityForm';
import EvaluationForm from './components/EvaluationForm';
import FellowEvaluations from './components/FellowEvaluations';
import EvaluationHistory from './components/EvaluationHistory';
import Quiz from './components/Quiz';
import ESASForm from './components/ESASForm';
import DTSForm from './components/DTSForm';
import InstallPrompt from './components/InstallPrompt';

// Public route — accessible without login
if (window.location.pathname === '/esas') {
  const root = document.getElementById('root');
  import('react-dom/client').then(({ createRoot }) => {
    createRoot(root).render(<ESASForm />);
  });
}

function AppInner() {
  const { user, loading, isDirector, isChairman, profile } = useAuth();
  const isFaculty   = profile?.role === 'faculty';
  const isFellow    = profile?.role === 'fellow';
  const canEvaluate = (isDirector || isFaculty) && !isChairman;

  const [view, setView]             = useState('home');
  const [entries, setEntries]       = useState([]);
  const [activities, setActivities] = useState([]);
  const [editEntry, setEditEntry]   = useState(null);
  const [fetchError, setFetchError] = useState('');
  const [isMobile, setIsMobile]     = useState(window.innerWidth < 768);
  const [pendingEvals, setPendingEvals] = useState(0);
  const [showHomeEditor, setShowHomeEditor] = useState(false);
  const isFirstLogin = useRef(true);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchEntries = useCallback(async () => {
    if (!user) return;
    const { data, error } = await supabase.from('entries').select('*').eq('user_id', user.id).order('date', { ascending: false });
    if (error) setFetchError(error.message); else setEntries(data || []);
  }, [user]);

  const fetchActivities = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase.from('activities').select('*').eq('user_id', user.id).order('date', { ascending: false });
    setActivities(data || []);
  }, [user]);

  const fetchPendingEvals = useCallback(async () => {
    if (!user || !isFellow) return;
    const { data } = await supabase.from('evaluations').select('id').eq('fellow_id', user.id).eq('acknowledgment_status', 'pending');
    setPendingEvals((data || []).length);
  }, [user, isFellow]);

  useEffect(() => {
    if (user && isFellow) { fetchEntries(); fetchActivities(); fetchPendingEvals(); }
  }, [user, isFellow, fetchEntries, fetchActivities, fetchPendingEvals]);

  useEffect(() => {
    if (!user || !isFellow) return;
    const channel = supabase.channel(`entries-${user.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'entries', filter: `user_id=eq.${user.id}` }, fetchEntries)
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [user, isFellow, fetchEntries]);

  useEffect(() => {
    if (user && isFirstLogin.current) {
      setView('home');
      isFirstLogin.current = false;
    }
    if (!user) isFirstLogin.current = true;
  }, [user]);

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh', fontFamily:"'Inter',sans-serif", background:'#F5EFE6' }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ fontSize:32, color:'#C8956C', marginBottom:12 }}>✦</div>
        <div style={{ color:'#888' }}>Loading…</div>
      </div>
    </div>
  );

  if (!user) return <AuthPage />;

  const total    = entries.length;
  const approved = entries.filter(e => ['approved','approved_with_comments'].includes(e.approval_status)).length;
  const pending  = entries.filter(e => e.approval_status === 'pending').length;

  const navView = (v) => { if (!['new','activity','evaluate'].includes(v)) setEditEntry(null); setView(v); };
  const handleSaved         = () => { fetchEntries(); setEditEntry(null); navView('dashboard'); };
  const handleActivitySaved = () => { fetchActivities(); navView('log'); };
  const handleEvalSaved     = () => { navView(isFaculty ? 'faculty' : 'director'); };
  const handleEdit          = (entry) => { setEditEntry(entry); setView('new'); };

  const mainContent = (
    <div style={{ flex:1, background:'#F5EFE6', overflowY:'auto', paddingBottom: isMobile ? 70 : 0 }}>
      {fetchError && <div style={{ background:'#FFF0F0', border:'1px solid #F4A8A8', color:'#8A2D2D', padding:'10px 20px', fontSize:13 }}>⚠ Could not load entries. Please refresh.</div>}

      {view === 'home' && (
        <>
          <HomePage isMobile={isMobile} isDirector={isDirector} onEditHome={() => setShowHomeEditor(true)} />
          {showHomeEditor && <HomeEditor onClose={() => setShowHomeEditor(false)} isMobile={isMobile} />}
        </>
      )}

      {canEvaluate && view === 'evaluate' && <EvaluationForm onSaved={handleEvalSaved} onCancel={() => navView(isFaculty ? 'faculty' : 'director')} isMobile={isMobile} />}
      {isFaculty && view === 'faculty' && <FacultyDashboard isMobile={isMobile} onEvaluate={() => navView('evaluate')} />}
      {(isDirector || isChairman) && ['director','entries','analytics'].includes(view) && <DirectorDashboard view={view} isMobile={isMobile} isChairman={isChairman} onEvaluate={() => navView('evaluate')} />}
      {(isDirector || isChairman) && view === 'eval_history' && <EvaluationHistory isMobile={isMobile} />}
      {isDirector && view === 'quiz' && <Quiz isMobile={isMobile} onFinish={() => navView('director')} />}

      {isFellow && (
        <>
          {view === 'dashboard'   && <Dashboard entries={entries} activities={activities} setView={navView} setEditEntry={setEditEntry} onRefresh={fetchEntries} isMobile={isMobile} pendingEvals={pendingEvals} />}
          {view === 'new'         && <EntryForm existingEntry={editEntry} onSaved={handleSaved} onCancel={() => navView('log')} isMobile={isMobile} />}
          {view === 'activity'    && <ActivityForm onSaved={handleActivitySaved} onCancel={() => navView('dashboard')} isMobile={isMobile} />}
          {view === 'log'         && <CaseLog entries={entries} activities={activities} onEdit={handleEdit} onRefresh={fetchEntries} onRefreshActivities={fetchActivities} isMobile={isMobile} />}
          {view === 'progress'    && <Progress entries={entries} isMobile={isMobile} />}
          {view === 'evaluations' && <FellowEvaluations isMobile={isMobile} />}
          {view === 'quiz'        && <Quiz isMobile={isMobile} onFinish={() => navView('home')} />}
        </>
      )}
    </div>
  );

  if (isMobile) return (
    <div style={{ display:'flex', flexDirection:'column', minHeight:'100vh', fontFamily:"'Inter',sans-serif" }}>
      <div style={{ background:'#3D4F5C', padding:'12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ color:'#C8956C', fontSize:18 }}>✦</span>
          <span style={{ fontFamily:"'Playfair Display',serif", fontSize:15, color:'#F5EFE6' }}>Palliative Log</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          {pendingEvals > 0 && <div style={{ background:'#C8956C', color:'#fff', borderRadius:'50%', width:20, height:20, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700 }}>{pendingEvals}</div>}
          <div style={{ width:28, height:28, borderRadius:'50%', background:'#C8956C', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'Playfair Display',serif", fontSize:13, color:'#fff' }}>{profile?.full_name?.[0] || '?'}</div>
          <span style={{ fontSize:12, color:'rgba(245,239,230,0.7)' }}>{profile?.full_name?.split(' ')[1]}</span>
        </div>
      </div>
      {mainContent}
      {view !== 'quiz' && <MobileNav view={view} setView={navView} isDirector={isDirector || isChairman} isFaculty={isFaculty} isFellow={isFellow} pending={pending} pendingEvals={pendingEvals} canEvaluate={canEvaluate} />}
      <InstallPrompt />
    </div>
  );

  return (
    <div style={{ display:'flex', minHeight:'100vh', fontFamily:"'Inter',sans-serif" }}>
      <Sidebar view={view} setView={navView} stats={{ total, approved, pending }} pendingEvals={pendingEvals} isFellow={isFellow} />
      {mainContent}
    </div>
  );
}

export default function App() {
  // Public route — no login needed
  if (window.location.pathname === '/esas') return <ESASForm />;
  if (window.location.pathname === '/dts') return <DTSForm />;
  return <AuthProvider><AppInner /></AuthProvider>;
}
